/**
 * Set or change the price of one draft line (round 4 of the minimal redesign, 12 Oct
 * 2026), opened from the row menu. A price, its currency and an optional note; the
 * hint says what I2Q found, so the person sees what they replace.
 *
 * The write is `writes.setPrice` (an "edit" override with the price fields only, so a
 * reviewed quantity stays). The backend then puts the price into the same line fields
 * the engine's price uses: the checks, the quotation preview, the reply and the F&O
 * draft check follow on the next read. Nothing is sent and nothing reaches F&O.
 *
 * The form's pieces come from `useSetPriceForm`, and the dialog places them into the DS
 * Dialog's title, description, body and footer. A server render of a DS Dialog is empty
 * (its popup lives in a portal), so the render tests place the same pieces into plain
 * elements and read the same fields, hint and buttons.
 */

import { Button, Dialog, Field, Input, Typography, useToastManager } from "@norican/react";
import { type ReactNode, useState } from "react";

import { effectiveItem } from "@/features/case-review/line-semantics";
import { type CaseWrites } from "@/features/case-review/use-case-writes";
import {
  currencyInputError,
  engineFoundText,
  priceAmountError,
  priceInputError,
  priceToWire,
} from "@/lib/line-price";
import { logger } from "@/lib/logger";
import { type QuoteCaseDetail, type QuoteLine } from "@/lib/mock";
import { ACTION_LABELS, SAVE_TOASTS } from "@/lib/sales-flow";
import { useDialogUsage } from "@/lib/usage-react";

export interface SetPriceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  detail: QuoteCaseDetail;
  line: QuoteLine;
  /** Position of the line in the stored draft (what the API expects). */
  lineIndex: number;
  writes: CaseWrites;
}

/** The engine's price of a line: behind a manual price it sits on the override. */
export function enginePriceOf(line: QuoteLine): {
  unitPrice: string | null;
  currency: string | null;
} {
  if (line.price?.source === "sales") {
    return {
      unitPrice: line.override?.engine_unit_price ?? null,
      currency: line.override?.engine_currency ?? null,
    };
  }
  if (line.price?.status === "found") {
    return { unitPrice: line.price.unitPrice, currency: line.price.currency };
  }
  return { unitPrice: null, currency: null };
}

/**
 * The currency the box starts with: the line's engine currency, else any found line's,
 * else the currency a person set on any line of the case (round 5), else empty.
 */
export function startCurrency(detail: QuoteCaseDetail, line: QuoteLine): string {
  const own = enginePriceOf(line).currency;
  if (own) {
    return own;
  }
  const found = detail.lines.find(
    (other) => other.price?.status === "found" && other.price.currency,
  );
  if (found?.price?.currency) {
    return found.price.currency;
  }
  const set = detail.lines.find(
    (other) => other.override?.currency || other.override?.engine_currency,
  );
  return set?.override?.currency ?? set?.override?.engine_currency ?? "";
}

/**
 * The price the box starts with: the price a person set on the line, else the price I2Q
 * found, else nothing.
 */
export function startPrice(line: QuoteLine): string {
  const stored =
    line.price?.source === "sales" ? line.price.unitPrice : enginePriceOf(line).unitPrice;
  const amount = Number((stored ?? "").trim());
  if ((stored ?? "").trim() === "" || !Number.isFinite(amount) || amount <= 0) {
    return "";
  }
  // Two decimals, as the box takes them: F&O holds four ("412.5000"). A price with more
  // (0.0375 a piece) is not rounded into the box: the hint under it says what I2Q found.
  const shown = amount.toFixed(2);
  return Number(shown) === amount ? shown : "";
}

/** True when a typed amount is the same figure as a stored one ("73" and "73.00"). */
function sameAmount(typed: string, stored: string | null | undefined): boolean {
  const a = Number(typed.trim().replace(",", "."));
  const b = Number((stored ?? "").trim());
  return typed.trim() !== "" && (stored ?? "").trim() !== "" && Number.isFinite(a) && a === b;
}

/** True when the boxes hold the price I2Q found, in its currency. */
function sameAsEngine(
  engine: { unitPrice: string | null; currency: string | null },
  price: string,
  currency: string,
): boolean {
  return (
    sameAmount(price, engine.unitPrice) &&
    currency.trim().toUpperCase() === (engine.currency ?? "").trim().toUpperCase()
  );
}

/**
 * True when "Save price" would change nothing: the line carries a price a person set,
 * and the boxes hold that price, that currency and that note.
 */
export function priceUnchanged(
  line: QuoteLine,
  typed: { price: string; currency: string; note: string },
): boolean {
  if (line.price?.source !== "sales") {
    return false;
  }
  return (
    sameAmount(typed.price, line.price.unitPrice) &&
    typed.currency.trim().toUpperCase() === (line.price.currency ?? "").trim().toUpperCase() &&
    typed.note.trim() === (line.override?.price_note ?? "").trim()
  );
}

/** What the dialog shows, in the order the DS Dialog places it. */
export interface SetPriceForm {
  /** "Set price", or "Change price" when a person set one already. */
  title: string;
  /** The customer's words and the item. */
  description: string;
  fields: ReactNode;
  /** The last refusal of a write, or null. */
  error: ReactNode;
  footer: ReactNode;
  /** Saves the price in the boxes (the dialog's form calls it on Enter). */
  submit: () => void;
}

/** The state and the pieces of the price form of one line. */
export function useSetPriceForm({
  detail,
  line,
  lineIndex,
  writes,
  onClose,
  onSaved,
}: Omit<SetPriceDialogProps, "open" | "onOpenChange"> & {
  onClose: () => void;
  /** Called when the price was stored, before the form closes (the usage log). */
  onSaved?: () => void;
}): SetPriceForm {
  const toast = useToastManager();
  const manual = line.price?.source === "sales";
  const engine = enginePriceOf(line);
  // The box starts with a price: the one a person set ("Change price"), else the one I2Q
  // found. It is selected on focus, so the first key replaces it (ease round, 13 Oct
  // 2026: the box was empty, and typing over an old price added to it).
  const [price, setPrice] = useState(() => startPrice(line));
  const [currency, setCurrency] = useState(() => startCurrency(detail, line));
  const [note, setNote] = useState(line.override?.price_note ?? "");
  const error = priceInputError(price, currency);
  const shown = effectiveItem(line);
  // The price is per the price's own unit; the customer's unit ("N.") can be one I2Q
  // does not know (tester round 5, 12 Oct 2026).
  const unit = line.price?.uom || (line.unit ?? "").trim() || "unit";

  const save = () => {
    if (error) {
      return;
    }
    if (
      priceUnchanged(line, { price, currency, note }) ||
      (!manual && sameAsEngine(engine, price, currency) && note.trim() === "")
    ) {
      // The price on the line already: nothing is sent (a price a person sets replaces
      // I2Q's price and its checks, so the same figure is not saved as a person's).
      toast.add({
        title: SAVE_TOASTS.noChange.title,
        description: SAVE_TOASTS.noChange.text,
        type: "success",
      });
      onClose();
      return;
    }
    logger.debug("[case] save line price", { case: detail.id, line: line.id, manual });
    writes.setPrice(
      lineIndex,
      line.lineKey ?? "",
      {
        unit_price: priceToWire(price),
        currency: currency.trim().toUpperCase(),
        ...(note.trim() ? { price_note: note.trim() } : {}),
      },
      () => {
        onSaved?.();
        onClose();
      },
    );
  };

  return {
    title: manual ? ACTION_LABELS.changePrice : ACTION_LABELS.setPrice,
    description: [line.enquiryText, [shown.item, shown.name].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(" · "),
    fields: (
      <>
        <Field
          label={`Price per ${unit}`}
          hint={engineFoundText(engine.unitPrice, engine.currency)}
          // The empty box says nothing yet; Save stays off until a price is typed.
          error={price.trim() === "" ? undefined : priceAmountError(price)}
        >
          <Input
            value={price}
            inputMode="decimal"
            onChange={(event) => setPrice(event.target.value)}
            // The old value is selected: the first key replaces it.
            onFocus={(event) => event.target.select()}
            aria-label="Price"
            autoFocus
            endSlot={currency.trim().toUpperCase() || undefined}
          />
        </Field>
        <Field
          label="Currency"
          hint="Three letters, like EUR or GBP."
          // The sentence sits under this box once a price is typed, so an empty currency
          // (a line without any price in the case) says what is missing (round 5).
          error={price.trim() === "" ? undefined : currencyInputError(currency)}
        >
          <Input
            value={currency}
            maxLength={3}
            onChange={(event) => setCurrency(event.target.value.toUpperCase())}
            aria-label="Currency"
          />
        </Field>
        <Field label="Note (optional)" hint="For colleagues. For example: agreed on the phone.">
          <Input
            value={note}
            maxLength={300}
            onChange={(event) => setNote(event.target.value)}
            aria-label="Note"
          />
        </Field>
      </>
    ),
    error: writes.error ? (
      <Typography variant="body-sm" className="text-danger-fg" role="alert">
        {writes.error}
      </Typography>
    ) : null,
    footer: (
      <>
        <Button variant="ghost" tone="neutral" onClick={onClose}>
          {ACTION_LABELS.cancel}
        </Button>
        {/* The submit button of the dialog's form: Enter in a box saves too. */}
        <Button
          type="submit"
          tone="accent"
          variant="solid"
          loading={writes.busy}
          disabled={error !== null}
          onClick={(event) => {
            // Outside a form (a render of the pieces alone) the click still saves.
            if (!event.currentTarget.form) {
              save();
            }
          }}
        >
          {ACTION_LABELS.savePrice}
        </Button>
      </>
    ),
    submit: save,
  };
}

export function SetPriceDialog({ open, onOpenChange, ...rest }: SetPriceDialogProps) {
  // The usage log: opened, saved, or closed without a save. Never the price or the note.
  const usage = useDialogUsage("set_price", open, rest.detail.id);
  const form = useSetPriceForm({
    ...rest,
    onClose: () => onOpenChange(false),
    onSaved: usage.saved,
  });
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup size="sm" data-price-dialog={rest.line.id}>
          {/* A form, so Enter in a box saves. `contents`: the popup lays out its parts. */}
          <form
            className="contents"
            onSubmit={(event) => {
              event.preventDefault();
              form.submit();
            }}
          >
            <Dialog.Header>
              <Dialog.Title>{form.title}</Dialog.Title>
              <Dialog.CloseButton />
            </Dialog.Header>
            <Dialog.Body className="flex flex-col gap-4">
              <Dialog.Description>
                {/* A span: the description is a paragraph already. */}
                <Typography as="span" variant="body-sm" tone="muted" className="break-words">
                  {form.description}
                </Typography>
              </Dialog.Description>
              {form.fields}
              {form.error}
            </Dialog.Body>
            <Dialog.Footer className="flex flex-wrap justify-end gap-2">
              {form.footer}
            </Dialog.Footer>
          </form>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
